import {
  Adjust,
  AdjustConfig,
  AdjustEnvironment,
  AdjustEvent
} from "@awesome-cordova-plugins/adjust";
import { captureException } from "@/plugins/sentry";
import { deviceModule } from "@/store/device";
import { VUE_APP_ENV } from "@/config/VueAppEnv";
import { authStore } from "@/store/auth";
import { User } from "@/types/user/User";

export type EventName =
  | "APPLY_PHONE_NUMBER"
  | "SKIP_REGISTRATION"
  | "LOGIN"
  | "FINISH_INPUT_SMS_CODE"
  | "FINISH_INPUT_PASSWORD"
  | "FINISH_INPUT_BASIC_INFO"
  | "FINISH_INPUT_JOB_CHANGE"
  | "FINISH_INPUT_AVATAR_IMAGE"
  | "FINISH_INPUT_IDENTIFICATION_IMAGE"
  | "FINISH_APPLY_QUALIFICATION"
  | "CONFIRM_LEAVE"
  | "FINISH_LEAVE"
  | "UNINSTALL"
  | "FINISH_INPUT_BANK_ACCOUNT"
  | "START_APPLICATION"
  | "CONFIRM_APPLICATION_CONDITION"
  | "CONFIRM_CANCEL_POLICY"
  | "FINISH_APPLICATION"
  | "SHOW_LEAVE"
  | "REGISTER_FAVORITE"
  | "SHOW_ACCOUNT_INFO"
  | "SHOW_ACCOUNT_OTHER_INFO"
  | "SHOW_LEAVE_QUESTIONNAIRE"
  | "SHOW_HOW_TO_GUIDE"
  | "CLOSE_HOW_TO_GUIDE"
  | "SKIP_INTRODUCTION"
  | "SKIP_INPUT_BASIC_INFO"
  | "SKIP_INPUT_AVATAR_IMAGE"
  | "SKIP_INPUT_IDENTIFICATION_IMAGE"
  | "SKIP_APPLY_QUALIFICATION"
  | "SKIP_INPUT_BANK_ACCOUNT"
  | "SKIP_INPUT_JOB_CHANGE";

/*
 * イベント名とtokenのマッピング
 * tokenは https://dash.adjust.com/ にて設定されている
 * normalは、イベントが発生するたびに普通にカウントアップされる
 * uniqueは、ユーザ単位でユニーク（一度だけ）カウントされる
 */

type EventToken = Record<"normal" | "unique", string>;
const USERNAME_KEY = "username";

export const eventNameTokenMapping: Record<
  EventName,
  Record<"caitech_care" | "caitech_pharmacist", EventToken>
> = {
  APPLY_PHONE_NUMBER: {
    caitech_care: { normal: "fq8qbb", unique: "o3pved" },
    caitech_pharmacist: { normal: "uv13gg", unique: "c5fujk" }
  },
  SKIP_REGISTRATION: {
    caitech_care: { normal: "uwdxyn", unique: "fu2yq8" },
    caitech_pharmacist: { normal: "dwjgey", unique: "hlqds5" }
  },
  LOGIN: {
    caitech_care: { normal: "5g9hl0", unique: "qht52x" },
    caitech_pharmacist: { normal: "rjfpr6", unique: "rybxfu" }
  },
  FINISH_INPUT_SMS_CODE: {
    caitech_care: { normal: "b6vgvg", unique: "tbh89b" },
    caitech_pharmacist: { normal: "r9jtgr", unique: "863mh6" }
  },
  FINISH_INPUT_PASSWORD: {
    caitech_care: { normal: "mu9886", unique: "6pzske" },
    caitech_pharmacist: { normal: "iulz70", unique: "tyc331" }
  },
  FINISH_INPUT_BASIC_INFO: {
    caitech_care: { normal: "n8kji8", unique: "og6giz" },
    caitech_pharmacist: { normal: "kqe7lj", unique: "lxpnq1" }
  },
  FINISH_INPUT_JOB_CHANGE: {
    caitech_care: { normal: "4335ya", unique: "y4p92u" },
    caitech_pharmacist: { normal: "66hui7", unique: "ta2gi7" }
  },
  FINISH_INPUT_AVATAR_IMAGE: {
    caitech_care: { normal: "rt42o1", unique: "njk5t1" },
    caitech_pharmacist: { normal: "jpbtsd", unique: "n0l5uw" }
  },
  FINISH_INPUT_IDENTIFICATION_IMAGE: {
    caitech_care: { normal: "dbnzdl", unique: "pp4pnu" },
    caitech_pharmacist: { normal: "slki2m", unique: "vt5dsg" }
  },
  FINISH_APPLY_QUALIFICATION: {
    caitech_care: { normal: "ctpkrf", unique: "om4q3b" },
    caitech_pharmacist: { normal: "v9uzej", unique: "cxpn58" }
  },
  CONFIRM_LEAVE: {
    caitech_care: { normal: "l46w0c", unique: "dpkqk6" },
    caitech_pharmacist: { normal: "3f8dup", unique: "403dek" }
  },
  FINISH_LEAVE: {
    caitech_care: { normal: "hg5hio", unique: "56hx83" },
    caitech_pharmacist: { normal: "wszp33", unique: "x5wwv4" }
  },
  FINISH_INPUT_BANK_ACCOUNT: {
    caitech_care: { normal: "dr94bj", unique: "3qgj1q" },
    caitech_pharmacist: { normal: "3mnrxz", unique: "35s8tq" }
  },
  UNINSTALL: {
    caitech_care: { normal: "ef7poh", unique: "ufpj7k" },
    caitech_pharmacist: { normal: "eyyav5", unique: "37evee" }
  },
  START_APPLICATION: {
    caitech_care: { normal: "gfzvcc", unique: "jx2ubq" },
    caitech_pharmacist: { normal: "u3ivbr", unique: "x4dkad" }
  },
  CONFIRM_APPLICATION_CONDITION: {
    caitech_care: { normal: "6jl24m", unique: "gm46vh" },
    caitech_pharmacist: { normal: "1djt70", unique: "q9yvg9" }
  },
  CONFIRM_CANCEL_POLICY: {
    caitech_care: { normal: "d2iwr8", unique: "o0w8b6" },
    caitech_pharmacist: { normal: "qsxqfq", unique: "rnb91d" }
  },
  FINISH_APPLICATION: {
    caitech_care: { normal: "kgspgm", unique: "ugokkd" },
    caitech_pharmacist: { normal: "12v4tj", unique: "rqqsvl" }
  },
  SHOW_LEAVE: {
    caitech_care: { normal: "c1pap5", unique: "v2xoyj" },
    caitech_pharmacist: { normal: "5u9ejh", unique: "mddrjq" }
  },
  REGISTER_FAVORITE: {
    caitech_care: { normal: "gw0l4n", unique: "mhpc5b" },
    caitech_pharmacist: { normal: "i1cxxv", unique: "3lvw7e" }
  },
  SHOW_ACCOUNT_INFO: {
    caitech_care: { normal: "ybpzjt", unique: "frlsl5" },
    caitech_pharmacist: { normal: "qjy25a", unique: "se0oja" }
  },
  SHOW_ACCOUNT_OTHER_INFO: {
    caitech_care: { normal: "2en2z8", unique: "v9arpg" },
    caitech_pharmacist: { normal: "purmam", unique: "jgs4nr" }
  },
  SHOW_LEAVE_QUESTIONNAIRE: {
    caitech_care: { normal: "66cpu3", unique: "ga9dv1" },
    caitech_pharmacist: { normal: "p0i8fx", unique: "kofcux" }
  },
  SHOW_HOW_TO_GUIDE: {
    caitech_care: { normal: "7832fh", unique: "jghzcn" },
    caitech_pharmacist: { normal: "zc111x", unique: "98s48h" }
  },
  CLOSE_HOW_TO_GUIDE: {
    caitech_care: { normal: "slxhpl", unique: "8aojsa" },
    caitech_pharmacist: { normal: "t3a0yb", unique: "wkqhu9" }
  },
  SKIP_INTRODUCTION: {
    caitech_care: { normal: "38sqzk", unique: "z73z3l" },
    caitech_pharmacist: { normal: "r99q8e", unique: "251qfa" }
  },
  SKIP_INPUT_BASIC_INFO: {
    caitech_care: { normal: "4gwsvs", unique: "drztca" },
    caitech_pharmacist: { normal: "xz5kqh", unique: "hjqa5p" }
  },
  SKIP_INPUT_AVATAR_IMAGE: {
    caitech_care: { normal: "fr60uf", unique: "w2ew2k" },
    caitech_pharmacist: { normal: "4zar19", unique: "al4b4w" }
  },
  SKIP_INPUT_IDENTIFICATION_IMAGE: {
    caitech_care: { normal: "i32mil", unique: "y09jro" },
    caitech_pharmacist: { normal: "ulf2k4", unique: "pk6krk" }
  },
  SKIP_APPLY_QUALIFICATION: {
    caitech_care: { normal: "ks6ytl", unique: "u32uix" },
    caitech_pharmacist: { normal: "k8kh3a", unique: "hlndcf" }
  },
  SKIP_INPUT_BANK_ACCOUNT: {
    caitech_care: { normal: "oojib3", unique: "3r6psw" },
    caitech_pharmacist: { normal: "umh6mv", unique: "z67a38" }
  },
  SKIP_INPUT_JOB_CHANGE: {
    caitech_care: { normal: "uktsn8", unique: "5tqpq7" },
    caitech_pharmacist: { normal: "57phyd", unique: "658e7g" }
  }
};

export const initializeAdjust = () => {
  try {
    // @ts-ignore
    if (!window.cordova) return;
    const appToken = authStore.isCaitechCareProduct
      ? process.env.VUE_APP_ADJUST_APP_TOKEN
      : process.env.VUE_APP_ADJUST_APP_TOKEN_CAITECH_PHARMACIST;
    if (!appToken) {
      captureException(new Error("Adjust Token is not defined."));
      return;
    }
    const env =
      VUE_APP_ENV === "prod"
        ? AdjustEnvironment.Production
        : AdjustEnvironment.Sandbox;

    const adjustConfig = new AdjustConfig(appToken, env);
    // @ts-ignore
    if (Adjust.initSdk) {
      // @ts-ignore
      Adjust.initSdk(adjustConfig);
    } else {
      // adjust-sdk 5系でも動作するようにする
      // @ts-ignore
      Adjust.create(adjustConfig);
    }

    // // NOTE: アンインストール計測のために必要（現プランだと有効ではないため一旦コメントアウト。今後有効化される予定なので残しておく）
    // // @ts-ignore
    // const fcmToken = window.FirebasePlugin?.getToken();
    // if (fcmToken) Adjust.setPushToken(fcmToken);

    console.log(`adjust initialized as ${env}`);
  } catch (error) {
    captureException(error as Error);
  }
};

export const requestTrackingAuthorizationWithCompletionHandler = async () => {
  try {
    // @ts-ignore
    if (!window.cordova || !deviceModule.isIos) return;
    // @ts-ignore
    if (Adjust.requestAppTrackingAuthorization) {
      // @ts-ignore
      await Adjust.requestAppTrackingAuthorization();
    } else {
      // adjust-sdk 5系でも動作するようにする
      // @ts-ignore
      await Adjust.requestTrackingAuthorizationWithCompletionHandler();
    }
  } catch (error) {
    captureException(error as Error);
  }
};

export const sendAdjustEvent = async (
  eventName: EventName,
  params: Record<string, string> = {}
) => {
  try {
    const productSlug = authStore.product.slug as
      | "caitech_care"
      | "caitech_pharmacist";
    const tokens = eventNameTokenMapping[eventName][productSlug];
    if (!tokens)
      throw new Error(`adjust event token for ${eventName} not found.`);

    console.debug(
      `SEND_ADJUST_EVENT: ${eventName} params:${JSON.stringify(
        params
      )} tokens: ${JSON.stringify(tokens)}`
    );

    // @ts-ignore
    if (!window.cordova || (await Adjust.isEnabled()) === false) return;

    // NOTE: 通常カウント・ユニークカウント、両方のイベントを送信する ※Adjust側でユニーク化する
    for (const token of [tokens.normal, tokens.unique]) {
      const adjustEvent = new AdjustEvent(token);
      for (const key in params) {
        adjustEvent.addCallbackParameter(key, params[key]);
      }
      Adjust.trackEvent(adjustEvent);
    }
  } catch (error) {
    captureException(error as Error);
  }
};

export const setAdjustParameterUsername = async (user: User | null) => {
  if (!window.cordova || (await Adjust.isEnabled()) === false) return;

  if (!user?.username) return;
  Adjust.addGlobalCallbackParameter(USERNAME_KEY, user.username);
};

export const removeAdjustParameterUsername = async () => {
  if (!window.cordova || (await Adjust.isEnabled()) === false) return;

  Adjust.removeGlobalCallbackParameter(USERNAME_KEY);
};
